import { adminApi } from './api';

// The manual-dialer switch (plans/callbacks/dialer-v1.md §5): one settings row behind
// the admin proxy. `mode` is the kill switch checked at token mint AND at /voice;
// `pool_id` is the hidden filing pool stamped on manual call rows, created by the core
// the first time the switch is saved as live (read-only here, never typed in);
// `hours_gate` refuses out-of-hours dials; `transcripts_enabled` lets manual
// transcripts flow downstream (leadflow, dispositions, DNC) — off until the canary.
export type ManualDialerMode = 'off' | 'live';

// Which Do Not Contact blocks an SDR may call through after acknowledging why the
// lead is blocked (plans/callbacks/dialer-v1.2-dnc-callbacks.md): every block refuses
// (today's rule); an information request alone, three days after the call; every
// cooldown but never someone who asked us to stop; or every verdict. A block for every
// client stays hard at every level.
export type DncPolicy = 'block_all' | 'allow_info_requests' | 'allow_cooldowns' | 'allow_all';

export interface ManualDialerSettings {
  mode: ManualDialerMode;
  pool_id: number | null;
  hours_gate: boolean;
  transcripts_enabled: boolean;
  // The dial pad (a number nobody has) for every signed-in SDR, not only admins and
  // SDR managers. Optional: an older core answers without it.
  any_number_for_all?: boolean;
  // Optional: an older core answers without it, which reads as `block_all`.
  dnc_policy?: DncPolicy;
  updated_at: string;
}

export interface ManualDialerSettingsResponse {
  settings: ManualDialerSettings;
  // What this node's cache is serving — compared to the row to spot a cold cache.
  effective: Pick<
    ManualDialerSettings,
    'mode' | 'pool_id' | 'hours_gate' | 'transcripts_enabled' | 'any_number_for_all' | 'dnc_policy'
  >;
  modes: ManualDialerMode[];
  // The levels this core knows, strictest first. Absent on an older core: no picker.
  dnc_policies?: DncPolicy[];
}

// No `pool_id`: the filing pool is server-managed (created on the first activation,
// then immutable), and the core does not accept it.
export type ManualDialerSettingsPatch = Partial<
  Pick<
    ManualDialerSettings,
    'mode' | 'hours_gate' | 'transcripts_enabled' | 'any_number_for_all' | 'dnc_policy'
  >
> & {
  // The activation marker: sent with every `mode: 'live'` by THIS console, whose
  // confirm says every signed-in SDR gains the dialer. The core creates the filing pool
  // (the step that actually entitles the fleet) only when it is present, so a console
  // cached from before this build — whose confirm promised roster-only — cannot.
  entitle_every_sdr?: true;
};

export const getManualDialerSettings = (): Promise<ManualDialerSettingsResponse> =>
  adminApi.get<ManualDialerSettingsResponse>('/manual-dialer-settings');

// Sends only the fields the operator changed, so a stale editor cannot overwrite a
// sibling field another session just flipped.
export const updateManualDialerSettings = (
  patch: ManualDialerSettingsPatch,
): Promise<{
  settings: ManualDialerSettings;
  effective: ManualDialerSettingsResponse['effective'];
}> =>
  adminApi.patch<{
    ok: true;
    settings: ManualDialerSettings;
    effective: ManualDialerSettingsResponse['effective'];
  }>('/manual-dialer-settings', patch);
